/**
 * Kuratierte Hilfe-Inhalte für Novatus Sales Pro – ausführliche Variante.
 *
 * Pro Kontext:
 *   - title:     Kurzer Titel (1 Zeile)
 *   - summary:   3–5 Sätze, was dieser Bereich ist & wofür man ihn nutzt
 *   - tips:      Praxis-Tipps (8–12 Punkte, kein Limit, jeder ein voller Satz)
 *   - workflow:  Optional – nummerierte Schritt-für-Schritt-Anleitung
 *   - fields:    Optional – Erklärung der wichtigsten Eingabefelder
 *   - aiSeed:    Optionale vorgeschlagene Frage für den KI-Assistenten
 */
import { SALES_HELP, AUDIENCE_PATCH } from "./helpContentSales";

const BASE_HELP = {
  // ---------------------------------------------------------------------
  // Globale Bereiche
  // ---------------------------------------------------------------------
  "app.global": {
    title: "Novatus Sales Pro · Überblick",
    summary:
      "Diese App begleitet dich vom Erstkontakt bis zum abgeschlossenen Auftrag — " +
      "Telesales-Erstkontakt, Vor-Ort-Tech-Check, Heizlast-Schätzung, Angebot inkl. " +
      "Wirtschaftlichkeit, Vor-Ort-Unterschrift, Back-Office-Übergabe (17 Schritte) und " +
      "Installateur-Auftrag. Multi-mandantenfähig, offlinefähig, DSGVO-konform.",
    tips: [
      "Hilfe-Button (?) oben rechts im Header öffnet ein kontextsensitives Hilfe-Panel mit Erklärung, Schritt-für-Schritt-Workflow und Praxis-Tipps für genau den Bereich, in dem du dich gerade befindest.",
      "Im Hilfe-Panel stellt 'KI fragen (vorgeschlagen)' eine perfekt formulierte Frage an den KI-Assistenten — schneller als selbst tippen.",
      "Admins & Vertriebsdirektoren sehen im Hilfe-Panel zusätzlich einen 'Handbuch'-Button → personalisiertes PDF-Handbuch generieren.",
      "KI-Assistent (Sparkle-Symbol unten links) beantwortet Wärmepumpen-Fachfragen UND App-Bedienungsfragen mit Gemini 3 Flash.",
      "Offline-Modus: Daten werden lokal gespeichert und beim nächsten Online-Status automatisch synchronisiert.",
      "Push-Benachrichtigungen im Profil aktivieren — Statuswechsel, Wiedervorlagen und @-Mentions landen direkt auf deinem Gerät.",
      "Rollen: Vertrieb · Vertriebsleiter · Vertriebsdirektor · Admin · Telesales-Director/Agent · Installateur — die Menüpunkte passen sich automatisch an deine Rolle an.",
      "Bei Problemen den Feedback-Button (rechts unten) nutzen — Screenshots werden automatisch beigefügt.",
      "Demo-/Schulungsmodus: Admins können einen Trainings-Mode mit Demodaten aktivieren, ohne echte Projekte zu beeinflussen.",
      "Tour-Empfehlung für Neueinsteiger: 1) Dashboard → 2) Neues Projekt → 3) Tech-Check mit KI-Foto-Analyzer → 4) Angebot generieren → 5) Unterschreiben → 6) An Back-Office übergeben.",
    ],
    aiSeed: "Wie ist der typische Ablauf eines Wärmepumpen-Projekts vom Lead bis zur Schlussrechnung?",
  },

  // ---------------------------------------------------------------------
  // Dashboard / Listen
  // ---------------------------------------------------------------------
  "dashboard": {
    title: "Dashboard – Deine Tagesübersicht",
    summary:
      "Das Dashboard ist deine persönliche Startseite. KPI-Kacheln zeigen Live-Zahlen " +
      "(Projekte gesamt, in Bearbeitung, Angebote, Gewonnen). Darunter findest du die " +
      "zuletzt bearbeiteten Projekte, anstehende Termine und offene Aufgaben.",
    tips: [
      "Klick auf eine KPI-Kachel filtert die Projektliste direkt vor (z. B. „Gewonnen“ → alle gewonnenen).",
      "„Neues Projekt anlegen“ ist der wichtigste Button für den Außendienst-Alltag – große Touch-Fläche oben.",
      "Termine heute / morgen erscheinen oben, ältere im Verlauf.",
      "Erinnerungen werden zusätzlich als Push-Benachrichtigung gesendet, wenn aktiviert.",
      "Der „Tester-Handbuch (PDF)“-Button gibt dir das aktuelle Tester-Handbuch für UAT-Sessions.",
      "Im Verwaltungsbereich (nur Manager/Direktor/Admin) findest du Kanban, Team-Auswertung, Pipeline-Health und das Handbuch-Modul.",
    ],
    aiSeed: "Was zeigen die KPI-Kacheln auf dem Dashboard?",
  },
  "projects.list": {
    title: "Projektliste",
    summary:
      "Alle Projekte, für die du berechtigt bist. Vertrieb sehen ihre " +
      "eigenen Projekte, Vertriebsleiter zusätzlich die ihres Teams, Direktoren und " +
      "Admins sehen alles. Die Liste ist nach letzter Änderung sortiert.",
    tips: [
      "Die Suche durchsucht gleichzeitig: Kundenname, Kundennummer, Adresse (PLZ + Ort) und Angebotsnummer.",
      "Statusfilter (z. B. nur „Angebot versendet“) und Demo-Filter sind kombinierbar.",
      "Klick aufs Plus oben rechts legt ein neues Projekt an – Pflichtfelder werden im ersten Schritt abgefragt.",
      "Demo-Projekte (Training) sind farblich markiert und werden in den KPI-Kacheln nicht mitgezählt.",
      "Vertriebsleiter können oben rechts „Alle Personen“ anklicken, um nach einem bestimmten Vertrieb zu filtern.",
    ],
    aiSeed: "Wie filtere ich Projekte nach einem bestimmten Status?",
  },
  "manager.kanban": {
    title: "Kanban-Board – Pipeline-Übersicht",
    summary:
      "Das Kanban-Board zeigt die gesamte Vertriebs-Pipeline deines Teams. Jede Karte ist " +
      "ein Projekt, jede Spalte eine Phase: Lead → Vor-Ort-Aufnahme → Angebot erstellt → " +
      "Angebot versendet → Auftrag unterschrieben → Anzahlung erhalten → Auftragsbestätigung → " +
      "Material → Montage → Inbetriebnahme → Schlussrechnung → Abgeschlossen.",
    workflow: [
      "Karte greifen und in die nächste Spalte ziehen – das löst automatisch einen Chronik-Eintrag aus.",
      "Beim Verschieben in bestimmte Spalten werden automatisch Folge-Aufgaben erzeugt (z. B. „AZ1-Rechnung erstellen“ bei „Auftrag unterschrieben“).",
      "Rote Karten = Projekt > 14 Tage ohne Aktivität, gelbe = > 7 Tage – nutze sie als Frühwarnsystem.",
      "Filterfunktion oben hilft beim Fokussieren auf einen Vertrieb oder eine PLZ.",
    ],
    tips: [
      "Drag-and-Drop funktioniert in beide Richtungen – Manager dürfen auch rückwärts schieben.",
      "Karte anklicken öffnet das Projekt im Detail.",
      "Vertrieb sehen nur ihre eigenen Karten im Kanban.",
      "Bei großen Teams: nach Vertrieb filtern und „Sort by KPI“ nutzen.",
    ],
    aiSeed: "Wann sollte ich eine Karte zurück in eine frühere Spalte schieben?",
  },
  "manager.team": {
    title: "Team-Analytics",
    summary:
      "Vergleicht alle Vertrieb deines Teams in Kennzahlen: Anzahl Projekte, Win-Rate, " +
      "durchschnittlicher Angebotswert, Conversion-Zeit, ø Heizlast, ø Tage zum Auftrag. " +
      "Klick auf einen Namen öffnet die Einzel-Detailansicht mit Trendlinien.",
    tips: [
      "Zeitraum oben rechts auswählen: Monat / Quartal / Jahr / individuell.",
      "Demo-Projekte werden standardmäßig ausgeblendet (Toggle „Demo einbeziehen“ rechts).",
      "Vergleichswerte zum Vormonat in grün/rot direkt unter den Kennzahlen.",
      "Win-Rate = Gewonnen / (Gewonnen + Verloren). Archivierte/laufende Projekte zählen nicht.",
      "Conversion-Zeit = Tage von „Erstkontakt“ bis „Auftrag unterschrieben“.",
      "Export: Klick rechts oben auf das Download-Icon erzeugt eine CSV.",
    ],
    aiSeed: "Welche KPI ist für die Vertriebssteuerung am wichtigsten?",
  },
  "calendar": {
    title: "Kalender",
    summary:
      "Termine, Wiedervorlagen und Erinnerungen rund um deine Projekte. Du kannst " +
      "Einzeltermine erstellen, Termine an ein Projekt knüpfen und alle Termine als " +
      "ICS in Outlook/Google exportieren.",
    workflow: [
      "Klick in einen Tag → Schnell-Anlage-Dialog (Titel, Uhrzeit, optional Projekt-Bezug).",
      "Termine mit Projekt-Bezug erscheinen automatisch auch im Projekt-Tab „Aufgaben“.",
      "Per Wiederholungs-Regel (täglich/wöchentlich) wiederkehrende Termine anlegen.",
      "Erinnerung 1h / 1 Tag vorher – Push + optional E-Mail.",
    ],
    tips: [
      "Tasten-Shortcuts: T springt zu heute, ← / → wechseln den Monat.",
      "ICS-Export erzeugt eine .ics-Datei für externe Kalender.",
      "Doppelklick auf einen Termin öffnet das Bearbeiten-Modal.",
      "Bei Konflikten (mehrere Termine zur gleichen Uhrzeit) gibt es einen orange Warnstrich.",
    ],
    aiSeed: "Wie exportiere ich meinen Novatus-Kalender nach Outlook?",
  },
  "tasks": {
    title: "Aufgaben",
    summary:
      "Persönliche und projektbezogene To-dos mit Fälligkeitsdatum, Priorität, " +
      "Status und optionalem Projekt-Bezug. Aufgaben aus einem Projekt heraus " +
      "erstellt erscheinen auch in dieser zentralen Liste.",
    tips: [
      "Filter oben: alle / offen / erledigt / überfällig.",
      "Prioritäten: niedrig (grau), normal (blau), hoch (orange), dringend (rot).",
      "Beim Erledigen kannst du einen Abschluss-Kommentar hinterlassen – landet in der Projekt-Chronik.",
      "Wiederkehrende Aufgaben (z. B. wöchentlicher Pipeline-Review) werden automatisch dupliziert.",
      "Drag in der Liste verschiebt die Reihenfolge der gleichen Priorität.",
      "Bulk-Aktionen: Mehrere markieren, dann oben „Erledigen“ oder „Verschieben auf morgen“.",
    ],
    aiSeed: "Wie unterscheide ich „hoch“ und „dringend“ sinnvoll?",
  },

  // ---------------------------------------------------------------------
  // Projekt-Tabs (jeder Tab eigenständig)
  // ---------------------------------------------------------------------
  "project.customer": {
    title: "Kunde – Stammdaten",
    summary:
      "Hier erfasst du Anrede, Vor- und Nachname, Adresse (Straße/Hausnr/PLZ/Ort), " +
      "Telefon (Mobil + Festnetz), E-Mail, Eigentümerstatus und Gebäudetyp. Diese " +
      "Daten erscheinen später auf dem Angebot, in der Auftragsbestätigung und auf der " +
      "Schlussrechnung – also bitte sauber eintragen.",
    fields: [
      "Anrede + Titel: relevant für die korrekte Briefanrede im Angebot („Sehr geehrter Herr Dr. Müller“).",
      "Telefon im internationalen Format (+49…): wichtig für spätere SMS-Funktionen und das Wahl-Symbol in der App.",
      "Eigentümerstatus: Eigentümer / Mieter / Eigentümergemeinschaft – beeinflusst die KfW-458-Förderfähigkeit.",
      "Gebäudetyp: EFH / DHH / RH / MFH – steuert das angebotene WP-Paket.",
      "Adresse vollständig: Straße + Hausnummer + PLZ + Ort – die Postanschrift wandert 1:1 ins Angebot.",
    ],
    tips: [
      "Kunden-Nummer wird automatisch beim Anlegen vergeben (6-stellig, fortlaufend).",
      "Mit dem grünen „Speichern“-Button werden Änderungen sofort live (kein expliziter „Bearbeiten“-Modus).",
      "Bei MFH (Mehrfamilienhaus) später unbedingt im Tech-Check „Heizkreise“ korrekt erfassen.",
      "DSGVO: Der Kunde muss vor Angebotsversand der Datenverarbeitung zugestimmt haben – Häkchen unten.",
      "Falls Telefon und E-Mail leer sind, kann kein Angebot per E-Mail versendet werden.",
      "Land ist per Default Deutschland – bei AT/CH manuell anpassen.",
    ],
    aiSeed: "Welche Daten brauche ich vom Kunden für ein KfW-458-Angebot?",
  },
  "project.consumption": {
    title: "Verbrauch & Heizlast",
    summary:
      "Hier erfasst du den bisherigen Energieverbrauch (Öl, Gas, Pellets, Strom oder Holz). " +
      "Aus dem Verbrauch der letzten 12–24 Monate berechnen wir mit Vollbenutzungsstunden und " +
      "Warmwasser-Abzug die geschätzte Heizlast – die Grundlage für die richtige " +
      "Wärmepumpen-Größe und das passende WP-Paket.",
    fields: [
      "Energieträger: Öl / Gas / Pellets / Holz / Strom / Fernwärme – steuert die Umrechnungsfaktoren.",
      "Verbrauch p.a.: Liter Öl, m³ Gas, kg Pellets, kWh Strom – siehe Jahresabrechnung.",
      "Heizwert / Brennwert: bei Gas und Öl mind. 5–10 % Unterschied beim Energiegehalt.",
      "Warmwasser inkludiert: wenn ja → 20 % Abzug bei der Heizlastberechnung.",
      "Vollbenutzungsstunden: 1.800 h (gut gedämmt) … 2.200 h (Altbau) … 2.400 h (Passivhaus).",
    ],
    workflow: [
      "Letzte 12–24 Monate Verbrauchsdaten vom Kunden einholen (idealerweise zwei volle Heizperioden).",
      "Energieträger und Verbrauch eintragen – die App rechnet sofort in kWh um.",
      "Vollbenutzungsstunden je nach Gebäude-Standard auswählen.",
      "Warmwasser-Abzug aktivieren, wenn die Heizung auch das WW erwärmt.",
      "Die berechnete Heizlast siehst du in Echtzeit unten als Karte – sie steuert später das WP-Paket.",
    ],
    tips: [
      "Vorsicht bei Holz-Heizungen: Kunden schätzen den Verbrauch oft zu niedrig – frage nach der Liefermenge.",
      "Bei Kombi-Geräten (Gas + Solarthermie): Solarthermie-Anteil separat erfassen (Feld „Solar-Deckung“).",
      "Ein-Personen-Haushalt: WW-Anteil kann unter 20 % liegen – Abzug ggf. manuell justieren.",
      "Wenn der Kunde keine Rechnungen hat: Schätzung aus Wohnfläche × Faustwert (Altbau 120 kWh/m²a, Neubau 50 kWh/m²a).",
      "Die berechnete Heizlast ist eine GROBSCHÄTZUNG – die exakte Heizlast nach DIN EN 12831 erstellt der Heizungsbauer.",
      "Bei Auffälligkeiten (z. B. „Heizlast > 30 kW im EFH“) nochmal kritisch prüfen.",
    ],
    aiSeed: "Welcher Wert für die Vollbenutzungsstunden passt zu einem Haus aus den 80ern?",
  },
  "project.offer": {
    title: "Angebot erstellen & versenden",
    summary:
      "Hier generierst du das Wärmepumpen-Angebot. Auf Basis der berechneten Heizlast " +
      "schlägt die App automatisch das passende WP-Paket vor (Komplettpaket mit " +
      "WP-Innen-/Außeneinheit, Speicher, Hydraulik). Du kannst Pakete, Sonderpositionen " +
      "und Rabatte anpassen, das PDF generieren und per E-Mail versenden.",
    workflow: [
      "App schlägt anhand der Heizlast 1–2 WP-Pakete vor – auswählen oder ändern.",
      "Sonderpositionen hinzufügen (z. B. Kernbohrung, Schornsteinrückbau, neuer Zählerschrank).",
      "KfW-458-Förderung aktivieren, wenn förderfähig – Kalkulation berücksichtigt automatisch.",
      "Rabatt eintragen (z. B. Frühbestellrabatt 2 %).",
      "Vorschau prüfen → PDF generieren → in der Vorschau-E-Mail Empfänger + Betreff + Text checken → Versenden.",
    ],
    fields: [
      "WP-Paket: Komplettlösungen je Heizlastbereich (z. B. HEIKO Thermal 9 für 7 kW, LG Therma V R290 für 9 kW).",
      "Sonderpositionen: alles was nicht im Paket steckt (Pufferspeicher 800 L, FBH-Erweiterung, …).",
      "KfW-458: Bundesförderung bis 70 % der förderfähigen Kosten (max. 28.000 € Bemessungsgrenze — Rechtsstand ab 21.07.2026).",
      "Rabatt: prozentual oder als Festbetrag – wirkt auf das Brutto.",
      "Gültigkeitsdauer: per Default 30 Tage, anpassbar.",
    ],
    tips: [
      "Angebotsnummer wird automatisch im Format YYMMPP-NNNN vergeben (Jahr-Monat-Projekttyp-Laufnr).",
      "Das PDF enthält bis zur Vor-Ort-Abnahme einen Vorläufigkeits-Hinweis („Angebot vorbehaltlich Tech-Check“).",
      "E-Mail-Versand öffnet eine Vorschau mit konkretem Empfänger – kein versehentliches Senden.",
      "Wenn Telefon/E-Mail fehlen → Button ist deaktiviert und zeigt Hinweis im Kunden-Tab.",
      "Bei mehreren Angeboten pro Projekt: jedes hat eigene Angebotsnummer + Datum – Auflistung im Tab unten.",
      "Sonderpositionen können mit USt./Brutto/Netto frei kalkuliert werden – passend zur GoBD.",
      "Nach „Auftrag unterschrieben“ wird das Angebot read-only – Änderungen nur per neuer Angebotsversion.",
    ],
    aiSeed: "Welche WP-Pakete passen zu 12 kW Heizlast?",
  },
  "project.tech": {
    title: "Technischer Check – Vor-Ort-Aufnahme",
    summary:
      "Der Tech-Check ist das HERZ der mobilen App. 10 Sektionen führen dich strukturiert " +
      "durch eine vollständige Wärmepumpen-Aufnahme: Wohnraum, Hausansicht, Heizraum, " +
      "Aktuelle Heizung, Speicher, Zählerschrank, Leitungsführung, Transportweg, " +
      "Außeneinheit und Grundrisse. Pro Sektion: Foto-Klingeln + KI-Auswertung + Detail-Felder.",
    workflow: [
      "Sektion aufklappen (Chevron) – Foto-Klingeln zeigen, welche Bildmotive du aufnehmen solltest.",
      "1–5 Fotos pro Sektion mit der App-Kamera machen.",
      "Auf „KI-Auswertung“ tippen – Gemini 3 Flash analysiert die Fotos und schlägt Werte vor.",
      "Vorschläge per Checkbox auswählen → „Übernehmen“ überträgt sie in die Detail-Felder.",
      "Bei Bedarf: „Detail-Fragen anzeigen“ klickt die strukturierte Eingabemaske auf.",
      "Notizen und Sonderbeobachtungen ins Notizfeld eintragen – KI-Auswertung wird als Block angehängt, manuelle Notizen bleiben erhalten.",
    ],
    tips: [
      "KI-Foto-Auswertung schlägt Werte vor – DU entscheidest, was übernommen wird (Vorschläge mit Confidence ≥ 0.4 sind vorausgewählt).",
      "Detail-Fragen sind per Default eingeklappt – Button „Detail-Fragen anzeigen“ blendet sie ein.",
      "Foto-Klingeln helfen, kein Schlüsselbild zu vergessen (Typenschild, Schornstein, Zählerschrank…).",
      "Bei Zählerschrank-Fotos erkennt die KI Zählertyp, freie Teilungseinheiten (TE) und WP-Tauglichkeit (3-phasige Sicherung + RCD Typ B).",
      "Im Grundriss-Tab kannst du eine freie Frage an die KI stellen (z. B. „Berechne Wohnfläche und Heizlast bei Altbau“).",
      "Fotos werden offline gespeichert und beim nächsten Online-Status hochgeladen.",
      "Beim Photo-Annotator (Tab „Medien“) kannst du auf Bildern markieren, wo Pufferspeicher / Innen- / Außeneinheit hin sollen.",
      "Der Tech-Check-Fortschritt (oben als Prozent) misst Sektionen mit mind. 1 erfasstem Feld ODER 1 Foto.",
    ],
    aiSeed: "Welche Daten sind im Tech-Check besonders kritisch für die WP-Auslegung?",
  },
  "project.tasks": {
    title: "Aufgaben (Projekt-spezifisch)",
    summary:
      "To-dos zu genau diesem Projekt – z. B. „Angebot nachfassen“, „Termin bestätigen“, " +
      "„Förderantrag prüfen“, „Installations-Übergabe vorbereiten“. Erscheinen automatisch " +
      "auch in deiner zentralen Aufgabenliste und im Dashboard.",
    fields: [
      "Titel: kurz und handlungsorientiert – „Anrufen Hr. Müller wegen Heizraum“ statt „Heizraum“.",
      "Fälligkeit: Datum und optional Uhrzeit – Datum ohne Uhrzeit erscheint als Tagesaufgabe.",
      "Priorität: niedrig / normal / hoch / dringend – beeinflusst Sortierung und Farbe.",
      "Status: offen / in Bearbeitung / erledigt – „in Bearbeitung“ wird farblich gelb markiert.",
      "Verantwortlich: standardmäßig du selbst, kann an andere Teammitglieder zugewiesen werden.",
    ],
    tips: [
      "Tasks mit Fälligkeit erscheinen in der Tages-Übersicht im Dashboard.",
      "Beim Statuswechsel des Projekts (z. B. „Auftrag unterschrieben“) werden automatisch Folge-Tasks erzeugt.",
      "Per Long-Press auf eine Aufgabe öffnest du Schnell-Aktionen (Verschieben, Duplizieren, Löschen).",
      "Wiederkehrende Aufgaben können hier nicht angelegt werden – nutze dafür den Kalender.",
      "Bei Aufgaben-Zuweisung an einen Kollegen erhält dieser eine Push-Benachrichtigung.",
      "Eine erledigte Aufgabe landet in der Projekt-Chronik – kein Datenverlust.",
    ],
    aiSeed: "Welche Aufgaben sollte ich anlegen, nachdem ich ein Angebot versendet habe?",
  },
  "project.messages": {
    title: "Nachrichten – Interner Chat",
    summary:
      "Interner Chat zum Projekt zwischen dir, deinem Vertriebsleiter und ggf. dem " +
      "Installation. Sichtbar nur für die Projekt-Berechtigten – der KUNDE SIEHT DIESE " +
      "NACHRICHTEN NIE. Anhänge bis 25 MB möglich (zusätzliche Fotos, PDFs, Sprachnotizen).",
    tips: [
      "@-Mention triggert eine Push-Benachrichtigung beim erwähnten Kollegen (z. B. @M.Berger).",
      "Anhänge: Klick aufs Büroklammer-Icon → Foto, Video, Dokument oder Sprachnotiz hinzufügen.",
      "Nachrichten sind read-only nach 7 Tagen, damit keine Spuren verwischt werden (DSGVO).",
      "Sprachnotizen werden automatisch transkribiert (Whisper) – der Text erscheint unter dem Audio.",
      "Für externe Kommunikation mit dem Kunden NICHT diesen Chat nutzen – stattdessen Mail oder Anruf.",
      "Bei sensiblen Themen (z. B. Preisverhandlung) lieber persönlich – Chat ist auditierbar.",
    ],
    aiSeed: "Wen sollte ich bei einer Nachfrage zum Schornstein @mentionen?",
  },
  "project.media": {
    title: "Medien & Foto-Annotator",
    summary:
      "Zentrale Galerie aller Fotos, Videos und Skizzen zum Projekt. Du kannst Fotos " +
      "aufnehmen, hochladen, kategorisieren und – einzigartig – auf jedem Foto mit Stift " +
      "markieren, wo Pufferspeicher, Inneneinheit, Außeneinheit oder die Leitungsführung " +
      "geplant sind.",
    workflow: [
      "„Foto aufnehmen“ oder „Hochladen“ → wähle Kategorie (Wohnraum, Heizraum, Außen, …).",
      "Optional: Sprachnotiz aufnehmen – wird transkribiert und ist im Foto-Hover sichtbar.",
      "„Markieren“-Button auf einem Foto öffnet den Annotator mit 4 Quick-Markern (Pufferspeicher / Inneneinheit / Außeneinheit / Leitungsführung).",
      "Bild kann vor dem Annotieren in 90°-Schritten gedreht werden.",
      "Speichern → annotiertes Bild landet automatisch im Skizzen-Tab; Original bleibt unverändert.",
    ],
    tips: [
      "Quick-Marker setzen Pin + Label im jeweiligen Farbcode (blau/grün/orange/rot).",
      "Stift-Tools: 6 Farben, 4 Strichstärken, Eraser (legt das Originalfoto frei – nicht eine Farbe), Undo (bis 25 Schritte).",
      "Wenn du Grundrisse abfotografierst: vor dem Annotieren rotieren, damit „oben“ stimmt.",
      "Im Tab „Skizzen“ findest du alle annotierten Bilder + freie Skizzen.",
      "Im Tab „Videos“ landen alle Video-Aufnahmen – sie können Sprachnotizen ersetzen.",
      "Pro Projekt-Foto kannst du beliebig viele Annotations-Varianten erstellen – jede ist eine eigene Skizze.",
    ],
    aiSeed: "Wie zeichne ich am besten die Leitungsführung in einen Grundriss ein?",
  },
  "project.status": {
    title: "Status & öffentliches Tracking",
    summary:
      "Pipeline-Status des Projekts (Erstkontakt → Termin geplant → Vor-Ort-Aufnahme → " +
      "Technische Prüfung → Angebot erstellt → Angebot versendet → Nachfassen → Gewonnen / " +
      "Verloren / Archiviert). Statuswechsel werden automatisch in der Chronik dokumentiert.",
    workflow: [
      "Status oben rechts ändern – mit Bestätigungs-Dialog.",
      "Bei „Verloren“ musst du einen Grund angeben (z. B. „Preis zu hoch“, „Anderer Anbieter“) – wichtig für die Team-Auswertung.",
      "Bei „Gewonnen“ wird der öffentliche Tracking-Link aktiviert (siehe unten).",
      "„Archiviert“ entfernt das Projekt aus den KPIs ohne es zu löschen.",
    ],
    tips: [
      "Der öffentliche Tracking-Link kann dem Kunden gesendet werden – schafft Vertrauen und reduziert Nachfragen.",
      "Auf dem Tracking-Link sieht der Kunde NUR Projekt-Phase und ETA, KEINE internen Notizen oder Preise.",
      "Tracking-Link ist via Token gesichert – ohne Token kein Zugriff.",
      "Beim Statuswechsel wird – sofern aktiviert – eine E-Mail mit dem aktuellen Stand an den Kunden gesendet.",
      "Statussprünge (z. B. von „Erstkontakt“ direkt zu „Gewonnen“) werden in der Chronik markiert und vom System geflagged.",
    ],
    aiSeed: "Wann ist der richtige Zeitpunkt, den Tracking-Link an den Kunden zu senden?",
  },
  "project.history": {
    title: "Chronik – Audit-Trail",
    summary:
      "Vollständiger Audit-Trail aller Änderungen am Projekt: wer wann was geändert hat. " +
      "Auch automatische Aktionen (E-Mail-Versand, Status-Auto-Advance, KI-Übernahmen) sind " +
      "protokolliert. Bei Streitfällen oder Rückfragen die schnellste Wahrheit.",
    tips: [
      "Filter nach Aktion (Statuswechsel / Feld-Edit / Mail / Foto / Skizze) hilft beim Nachvollziehen.",
      "Filter nach Benutzer: nützlich, wenn man wissen will, was ein bestimmter Kollege geändert hat.",
      "Einträge können NICHT editiert oder gelöscht werden – Audit-Sicherheit hat Priorität.",
      "Bei DSGVO-Antrag „Recht auf Vergessen“ können Personendaten pseudonymisiert werden (Benutzerrolle: Admin).",
      "Die Chronik geht zurück bis zur Projektanlage – nichts geht verloren.",
      "Automatische Aktionen sind farblich grün hinterlegt, manuelle blau.",
    ],
    aiSeed: "Wie finde ich heraus, wer einen bestimmten Feldwert geändert hat?",
  },

  // ---------------------------------------------------------------------
  // Admin / Direktion
  // ---------------------------------------------------------------------
  "admin.users": {
    title: "Benutzerverwaltung",
    summary:
      "Anlegen, Rollen vergeben und Hierarchie zwischen Vertriebsdirektor → " +
      "Vertriebsleiter → Vertrieb pflegen. Installation werden separat " +
      "über die Installations-Tabelle gepflegt. Admins haben uneingeschränkte Rechte " +
      "auf alle Organisationen.",
    tips: [
      "Rollenänderung wirkt sofort – der betroffene User muss sich neu einloggen.",
      "Status „inaktiv“ sperrt den Zugang, behält aber alle Projekt-Zuordnungen.",
      "Vertriebsleiter dürfen nur User in ihrer eigenen Hierarchie verwalten.",
      "Beim Anlegen wird automatisch eine Begrüßungs-E-Mail versendet (sofern E-Mail eingetragen).",
      "Die Hierarchie kann per Drag-and-Drop angepasst werden – User unter einen neuen Vertriebsleiter ziehen.",
      "Demo-Accounts (für Schulungen) sind markiert und werden in den Statistiken ignoriert.",
      "Bei Rollenwechsel Admin → andere Rolle: vorher prüfen, dass mind. ein anderer Admin existiert.",
    ],
    aiSeed: "Was passiert mit den Projekten, wenn ich einen Vertrieb inaktiv schalte?",
  },
  "admin.packages": {
    title: "WP-Pakete verwalten",
    summary:
      "Verwaltung der Produkt-Pakete (Komplettlösungen für die Wärmepumpen-Installation) " +
      "mit Preis, Förderfähigkeit, Heizlast-Bereich und Hersteller-Komponenten. Beim Angebot " +
      "wird das passende Paket automatisch vorgeschlagen.",
    tips: [
      "Heizlast-Bereiche müssen lückenlos sein, sonst gibt es bei Grenzwerten keinen Auto-Vorschlag.",
      "Bei Preisänderungen werden bestehende Angebote NICHT rückwirkend aktualisiert.",
      "Pakete als „inaktiv“ markieren statt löschen, um historische Angebote zu erhalten.",
      "Jedes Paket kann mehrere Hersteller-Varianten enthalten (z. B. Viessmann / Stiebel-Eltron).",
      "USt-Satz pro Position einstellbar – relevant bei Lieferung + Montage.",
      "Bei §13b UStG-Reverse-Charge-Fällen den Steuersatz auf 0 % stellen + Hinweis im Angebotstext.",
      "Sonderaktions-Pakete können zeitlich befristet werden.",
    ],
    aiSeed: "Wie definiere ich Heizlast-Bereiche, damit keine Lücken entstehen?",
  },
  "admin.health": {
    title: "Pipeline-Health-Dashboard",
    summary:
      "Frühwarn-System für Direktoren und Admins: stagnierende Projekte, vergessene " +
      "Wiedervorlagen, überfällige Installation-Übergaben, Win-Rate-Einbrüche und offene " +
      "Anzahlungsrechnungen. Wöchentlicher Mail-Digest geht automatisch jeden Montag " +
      "um 07:00 Uhr an Admin & Vertriebsdirektoren.",
    tips: [
      "Schwellenwerte für Warnungen sind in „Vertriebs-Einstellungen“ konfigurierbar (Default: 7d / 14d).",
      "Rote KPIs verlinken direkt auf die betroffenen Projekte – keine Suche nötig.",
      "Digest-Empfänger und Versandzeit sind im Backend pro Organisation konfigurierbar.",
      "Button „Digest senden“ oben rechts triggert sofort einen Versand (für Tests).",
      "Stille Projekte = Projekte ohne Aktivität (keine Änderung, kein Foto, keine Nachricht) > X Tage.",
      "Verkauft ohne Tech-Check = Projekt im Status „Gewonnen“ oder höher, aber Tech-Check < 80 % erfasst.",
      "Bei Überfälligen Installation-Übergaben: zeigt Projekte, die >7d nach „Auftragsbestätigung“ noch keine Übergabe haben.",
    ],
    aiSeed: "Wie senke ich die Anzahl stiller Projekte in meinem Team?",
  },
  "admin.installers": {
    title: "Installation (externe Partner)",
    summary:
      "Externe Heizungsbau-Partner verwalten, Übergabe-Portale generieren und Aufträge nach " +
      "Bedarf zuweisen. Jeder Installation hat eine eigene Upload-URL für den " +
      "Inbetriebnahme-Bericht. Installation sehen NIE Preise – nur technische Daten.",
    tips: [
      "Jeder Installation hat eine eigene Upload-URL für den Inbetriebnahme-Bericht.",
      "Sichtbarkeit: Installation sehen NIE Preise – nur technische Daten und Kunden-Adresse.",
      "Übergabe-PDF wird beim ersten Login digital signiert (DSGVO – Auftragsverarbeitungs-Vertrag).",
      "Bei einem Übergabe-Wechsel (Installation tauschen) bleibt die alte Zuordnung in der Chronik.",
      "Mehrere Standorte je Installation möglich – wichtig bei regionalen Aufträgen.",
      "Optionaler Service-Level (Reaktionszeit, Garantiedauer) wird im Installation-Profil hinterlegt.",
    ],
    aiSeed: "Welche Daten darf der Installation über das Portal sehen?",
  },
  "admin.handbook": {
    title: "Handbuch generieren",
    summary:
      "Personalisiertes PDF-Handbuch erstellen, das du gezielt an einen neuen Mitarbeiter " +
      "senden kannst. Der Inhalt richtet sich nach der gewählten Rolle: Vertrieb-Variante " +
      "fokussiert Tagesgeschäft, Vertriebsleiter-Variante zusätzlich Kanban + Team, " +
      "Direktor-Variante alles inkl. Health-Dashboard, Admin-Variante volle Konfiguration.",
    tips: [
      "Empfängername erscheint groß auf dem Deckblatt + persönlich in der Begrüßung.",
      "Die Rolle bestimmt den Umfang – Vertrieb-Variante ist kürzer (Fokus), Direktor länger.",
      "Das PDF kann lokal gespeichert oder per E-Mail an den Mitarbeiter weitergeleitet werden.",
      "Bei jedem neuen Mitarbeiter empfehlen wir, das Handbuch zusammen mit den Zugangsdaten zu senden.",
      "Updates am Handbuch (durch App-Updates) werden bei der nächsten Generierung automatisch übernommen.",
      "Das Handbuch enthält echte App-Screenshots – die Inhalte sind also immer aktuell.",
    ],
    aiSeed: "Welche Variante passt am besten für einen Neueinsteiger im Vertrieb?",
  },

  // ---------------------------------------------------------------------
  // Admin – Operations / Sicherheit / Daten-Hygiene
  // ---------------------------------------------------------------------
  "admin.wp-catalog": {
    title: "WP-Katalog (Wärmepumpen-Pakete)",
    summary:
      "Zentrale Pflege der HEIKO- und LG-Wärmepumpen-Pakete: Preise, technische Daten " +
      "(COP, Schallleistung, Maße, Gewicht, Kältemittel), Heizlast-Bracket für die " +
      "Auto-Empfehlung im Angebot, Aktiv-Status. Preisänderungen wirken sofort in NEUEN " +
      "Angeboten — Altangebote behalten ihren damaligen Preis (Audit-Sicherheit).",
    workflow: [
      "Liste aller Pakete (HEIKO + LG getrennt) sortiert nach kW-Klasse.",
      "Stift-Icon öffnet das Bearbeiten-Modal mit allen Stamm- und Tech-Daten.",
      "Preis ändern → Speichern → wirkt sofort in neuen Angeboten.",
      "Mülleimer-Icon deaktiviert das Paket (Soft-Delete) — Altangebote bleiben unverändert.",
      "Neues Paket über den grünen 'Neues Paket'-Button anlegen.",
    ],
    tips: [
      "Bei Preisrunden (1-2x pro Jahr) hier zentral aktualisieren — Vertriebler arbeiten ohne Code-Deploy weiter.",
      "Verwendungs-Spalte zeigt pro Paket die Anzahl Angebote — vor dem Deaktivieren prüfen.",
      "Neue Modelle: lieber ein neues Paket anlegen statt umbenennen — sauberer Audit-Trail.",
      "Bracket-Lücken vermeiden: Min-kW des nächsten Pakets direkt nach Max-kW des vorherigen.",
      "Pro Tenant eigene Liste — Multi-Mandanten haben getrennte Kataloge.",
      "Kältemittel bleibt aus Umweltgründen wichtig (R290 = natürlich, R32 = synthetisch), aber der KfW-Effizienzbonus für natürliches Kältemittel ist ab 21.07.2026 entfallen.",
    ],
    aiSeed: "Wann sollte ich ein WP-Paket deaktivieren statt löschen?",
  },
  "admin.promo-codes": {
    title: "Promo-Codes verwalten",
    summary:
      "Erstelle, bearbeite und deaktiviere Promo-Codes (z.B. 'FRUEHLING25' für 25% " +
      "Rabatt). Vertriebler geben Codes im Angebot ein — die App prüft Gültigkeit, " +
      "Min-Bestellwert, Max-Einlösungen und wendet den Rabatt automatisch an.",
    fields: [
      "Code: case-sensitive Großbuchstaben empfohlen (z.B. WAERME2026).",
      "Typ: Prozent oder Festbetrag.",
      "Wert: Zahl ohne Einheit (10 für 10% oder 500 für 500 EUR).",
      "Gültig-von / Gültig-bis: leer = unbegrenzt.",
      "Max-Einlösungen: 0 = unbegrenzt; sonst hartes globales Limit.",
      "Min-Bestellwert: Code wirkt nur ab diesem Brutto.",
    ],
    tips: [
      "Codes sind GLOBAL für den Tenant.",
      "Bei abgelaufenen Codes blockt die App den Versand automatisch.",
      "Promo-Code kann mit manuellem Rabatt kombiniert werden — additiv.",
      "Für Schulungen einen permanenten 'DEMO10'-Code anlegen.",
    ],
    aiSeed: "Wie kombiniere ich Promo-Codes mit dem Frühbestellrabatt?",
  },
  "admin.back-office": {
    title: "Back-Office-Cockpit (17-Schritte-Workflow)",
    summary:
      "Operatives Cockpit für die Auftragsabwicklung nach der Unterschrift. 17 Schritte " +
      "vom Auftragseingang bis zur Schlussrechnung: AB, Materialbestellung, Liefertermin, " +
      "Montagetermin, Inbetriebnahme, AZ1, AZ2, Anlagen-Übergabe, Schlussrechnung, " +
      "Förderantrag. Jeder Schritt hat Status, Verantwortlichen und Datum.",
    workflow: [
      "Vertrieb klickt 'An Back-Office übergeben' → Projekt erscheint als Karte.",
      "Karte annehmen → Verantwortliche Person zuweisen.",
      "Schritt abarbeiten → bei 'erledigt' läuft Push an Vertrieb.",
      "Bei 'blockiert' Grund eintragen — Vertriebsleiter wird per Mail benachrichtigt.",
      "Nach Schritt 17 → Projektstatus automatisch 'Abgeschlossen'.",
    ],
    tips: [
      "Kanban: alle 17 Spalten parallel — Drag-and-Drop erlaubt, Sprünge geflagged.",
      "Pro Schritt: Notizfeld + Datei-Upload (AB-PDF, Lieferschein, Inbetriebnahme-Protokoll).",
      "Bei AZ1/AZ2 Mahnstufen automatisch berechnet (7/14/21 Tage).",
      "Vertrieb sieht den Fortschritt read-only im Projekt-Tab Workflow.",
      "Rote Flagge bei > 30 Tagen in einem Schritt.",
    ],
    aiSeed: "Welcher Back-Office-Schritt blockiert Projekte am häufigsten?",
  },
  "admin.inbound-leads": {
    title: "Orientierungsangebote & Lead-Zuweisung",
    summary:
      "Online-Anfragen vom Web-Formular landen hier. Admins/Direktoren prüfen die Daten, " +
      "weisen einen Vertriebler zu und tracken den Conversion-Status. KPIs zeigen " +
      "Conversion-Rate und durchschnittliche Reaktionszeit.",
    workflow: [
      "Neuer Lead erscheint in der Inbox mit Adresse, Heizlast-Schätzung, Kontaktdaten.",
      "'Zuweisen' → Vertriebler aus Dropdown — er bekommt Push-Benachrichtigung.",
      "Vertriebler nimmt Kontakt auf → Status 'kontaktiert' + Notiz.",
      "Bei Auftrag → Status 'konvertiert' + Verknüpfung mit Projekt.",
      "Bei Absage → Status 'verloren' + Grund.",
    ],
    tips: [
      "Reaktionszeit-Ziel < 24h für maximale Conversion.",
      "Bulk-Zuweisung: mehrere Leads gleichzeitig vergeben.",
      "Dubletten werden automatisch markiert.",
      "Leads ohne Zuweisung > 30 Tage automatisch pseudonymisiert (DSGVO).",
    ],
    aiSeed: "Wie verbessere ich meine Lead-Conversion-Rate?",
  },
  "admin.presence": {
    title: "Live-Presence – Wer ist online?",
    summary:
      "Live-Übersicht aller User: wer gerade online ist (letzter Klick < 5 Min), welche " +
      "Route er nutzt, aktive Minuten heute und letzte 7 Tage. Drei KPI-Kacheln plus " +
      "24-h-Balkendiagramm zeigen den Tagespeak. Auto-Refresh alle 30 Sekunden.",
    workflow: [
      "Seite öffnen → KPIs + 24-h-Histogramm laden automatisch.",
      "Filter: alle / nur online / kürzlich aktiv / offline.",
      "Suchfeld: nach Name, Telefon oder E-Mail filtern.",
      "Spaltenkopf-Klick sortiert (z.B. nach 'Letzter Klick' oder 'Minuten heute').",
      "Online-Status am grünen Puls-Punkt erkennbar.",
    ],
    tips: [
      "Online-Threshold = 5 Min — länger inaktiv = offline.",
      "'Letzte Route' zeigt, in welchem App-Bereich der User gerade arbeitet.",
      "Stealth-/Super-Admins sind für reguläre Tenant-Admins NICHT sichtbar.",
      "Aktive Minuten = eindeutige Minuten-Buckets — keine Double-Counts.",
      "Bei akuten Support-Fällen siehst du, wo der User hängt — direkt anrufen statt raten.",
    ],
    aiSeed: "Warum erscheint ein User als offline, obwohl er gerade aktiv ist?",
  },
  "admin.test-data": {
    title: "Test-Daten-Manager – sauber aufräumen",
    summary:
      "Kontrolliertes Bulk-Löschen von Test-Konten, Test-Projekten und Test-Telesales-" +
      "Submissions. Vier Tabs (Konten, Projekte, Submissions, Audit-Log) zeigen alle " +
      "Items, die zur Test-Heuristik passen. Multi-Select + Confirm → Bulk-Delete inkl. " +
      "Cascading durch alle abhängigen Collections.",
    workflow: [
      "Tab wählen — Badge zeigt die Anzahl Treffer.",
      "Items per Checkbox auswählen oder 'Alle wählen'.",
      "Roten Button 'X löschen' klicken → AlertDialog erscheint.",
      "Bestätigen → Backend validiert Schutzregeln, löscht erlaubte Items + Toast.",
      "Audit-Log-Tab zeigt vergangene Lösch-Aktionen.",
    ],
    fields: [
      "Test-Pattern (Konten/Submissions): Name beginnt mit TEST_ / Test / Testkonto / Demo Tester ODER E-Mail-Domain example.com / test.com / mailinator / tempmail / yopmail.",
      "Test-Pattern (Projekte): customer_name beginnt mit TEST_ ODER source = Schulungs-Demo.",
      "Cascading-Delete: User-Delete entfernt Projekte/Customers/Offers/Photos/Videos/Tasks/Messages.",
    ],
    tips: [
      "Geschützt (NIE löschbar): Demo-Standard +491700000001-04, Türkei-1 (+4915255001000/002001/002002), Stealth-/Super-Admins, eigener Account.",
      "Vor Bulk-Delete: 'Alle wählen' prüfen, ob nur erwartete Treffer drin sind.",
      "Skip-Reason 'protected' → Item ist auf Schutzliste. 'cross_tenant' → kein Super-Admin. 'no_longer_test' → Pattern passt nicht mehr.",
      "Real-Workflow-Projekte werden NIE gelöscht — sicheres Fail-Safe.",
    ],
    aiSeed: "Welche Test-Konten kann ich sicher löschen ohne Demo-Mandanten zu zerstören?",
  },
  "admin.audit-log": {
    title: "Audit-Log – Chronik aller Admin-Aktionen",
    summary:
      "Vollständiger Audit-Trail aller administrativen Aktionen: Rollenwechsel, " +
      "User-Edits, Test-Daten-Löschungen, Workflow-Steps, Tenant-Eingriffe. Filter nach " +
      "Aktor, Action-Typ oder Zeitraum. Einträge sind unveränderlich.",
    tips: [
      "Filter 'Action-Typ' für schnelles Auffinden (z.B. user.update oder test_data.delete).",
      "Filter 'Aktor': zeigt nur Aktionen eines bestimmten Admins.",
      "Metadata-Feld enthält Diff-Snapshot (von/nach).",
      "Bei DSGVO-Auskunftsantrag: Filter nach target_id für vollständige Historie.",
      "Pro Tenant getrennt — Super-Admin sieht cross-tenant.",
    ],
    aiSeed: "Wie finde ich heraus, wer einen User-Status auf inaktiv gesetzt hat?",
  },
  "admin.call-lists": {
    title: "Telesales-Anruflisten verwalten",
    summary:
      "Anruflisten für Telesales-Agents erstellen und verteilen. CSV/Excel importieren " +
      "oder gefilterte Datensätze aus dem Lead-Pool ziehen. Die App verteilt an Director/" +
      "Agents und trackt Anruf-Status (kontaktiert / kein Interesse / Wiedervorlage).",
    workflow: [
      "'Neue Liste' → Name + Quelle (CSV-Import ODER Lead-Filter).",
      "CSV-Spalten-Mapping: Vorname, Nachname, Telefon, PLZ, Ort.",
      "Liste an Director zuweisen → Director verteilt an Agents.",
      "Agent ruft → Status + Notiz → bei Interesse direkt in Telesales-Capture.",
      "KPIs pro Liste: erreicht / nicht erreicht / Conversion-Rate.",
    ],
    tips: [
      "Beste Praxis: max. 50-80 Nummern pro Agent/Tag.",
      "Wiedervorlagen werden als Web-Push an den Agent ausgespielt.",
      "Dubletten automatisch markiert.",
      "Listen ohne Aktivität > 90 Tage werden archiviert.",
    ],
    aiSeed: "Wie groß sollte eine Anrufliste für einen Telesales-Agent pro Tag sein?",
  },
  "admin.tenants": {
    title: "Mandantenverwaltung (Multi-Tenant)",
    summary:
      "Super-Admin-Verwaltung aller Mandanten: Name, Domain, SMTP, aktivierte Module, " +
      "Branding, Anzahl User & Projekte, Speicher-Quota. Aktivieren/Deaktivieren ohne " +
      "Datenverlust.",
    tips: [
      "Nur SUPER-ADMINS sehen diese Seite.",
      "Beim Anlegen automatisch erster Tenant-Admin + Magic-Link.",
      "Deaktivieren = Login gesperrt; Daten bleiben erhalten.",
      "Multi-Mandanten-Trennung strikt: keine Cross-Tenant-Sichtbarkeit.",
      "Branding wirkt auf Login, Angebots-PDF-Header und E-Mail-Footer.",
    ],
    aiSeed: "Wie lege ich einen neuen Mandanten für eine Partner-Organisation an?",
  },
  "superadmin.tenants": {
    title: "Super-Admin Tenant-Übersicht",
    summary:
      "Globale Sicht auf ALLE Mandanten: aktive User, Projekte/Monat, Umsatz-Estimate, " +
      "letzte Aktivität. Zentrale Steuerzentrale für die SaaS-Operation.",
    tips: [
      "Klick auf Mandanten öffnet Detail-Analytik.",
      "Plan-Wechsel (Basic/Pro/Enterprise) wirkt sofort.",
      "Tenant-Status: Aktiv / Pausiert / Gekündigt.",
      "Bei verdächtigem API-Spike: oranger Warn-Indikator.",
      "Lösch-Button entfernt einen Tenant endgültig nach 30 Tagen Karenz.",
    ],
    aiSeed: "Was muss ich beachten, bevor ich einen Mandanten kündige?",
  },
  "admin.api-keys": {
    title: "API-Keys & Integrationen",
    summary:
      "Zentrale Pflege aller externen API-Schlüssel: Twilio (SMS+Voice), Stripe, SMTP, " +
      "Emergent LLM (Gemini/OpenAI/Claude). Plus interne Webhook-URLs.",
    tips: [
      "Keys werden server-side encrypted — niemals im Log sichtbar.",
      "Test-Modus vs. Live-Modus pro Provider — vor Umschalten gut testen.",
      "Bei Key-Verlust: neu im Provider-Dashboard generieren, alten widerrufen.",
      "Webhook-URL braucht HTTPS — sonst Liefer-Fehler.",
      "Audit-Log zeigt wer wann welchen Key getauscht hat.",
    ],
    aiSeed: "Was tun wenn ein API-Key kompromittiert ist?",
  },
  "admin.compliance": {
    title: "Compliance & DSGVO-Tools",
    summary:
      "Werkzeugkasten für rechtssichere Datenhaltung: DSGVO-Anträge (Auskunft/Löschen/" +
      "Berichtigung), Pseudonymisierung, Exporte für Wirtschaftsprüfer, Anti-Brute-Force, " +
      "Cookie-Banner-Config.",
    tips: [
      "DSGVO-Antrag bearbeitet alle Collections gleichzeitig.",
      "Pseudonymisierung ist irreversibel — Namen werden zu Hashes.",
      "Export für Prüfer enthält nur abgeschlossene Projekte.",
      "Brute-Force-Sperre: 5 Versuche → 15 Min Sperre.",
    ],
    aiSeed: "Wie reagiere ich auf einen DSGVO-Auskunftsantrag?",
  },
  "admin.penalties": {
    title: "Pönale – Vertragsstrafen für Installateure",
    summary:
      "Berechnet und verwaltet Vertragsstrafen für Installateurs-Verzögerungen: " +
      "Inbetriebnahme-Datum überschritten, Mangel-Häufung, Beschwerden. Optionale " +
      "Stripe-Auto-Abbuchung.",
    tips: [
      "Pönale-Schema pro Vertragstyp einstellbar (z.B. 1% pro Werktag Verzug, max 10%).",
      "Manuelle Anpassung möglich — Audit-Log dokumentiert.",
      "Streitfälle: Pönale 'pausiert' setzen.",
      "Stripe-Auto benötigt aktives SEPA-Mandat.",
    ],
    aiSeed: "Wie kommuniziere ich eine Pönale gegenüber dem Installateur?",
  },
  "admin.partner-profiles": {
    title: "Partner-Profile (Installateurs-Stammdaten)",
    summary:
      "Stammdaten der externen Installateurs-Partner: Firma, Inhaber, Adresse, Steuer-Nr, " +
      "AGB, Service-Region (PLZ-Bereiche), Garantie, Versicherungsnachweis. Wird bei Lead-" +
      "Auction & Auftragsverteilung als Match-Kriterium genutzt.",
    tips: [
      "PLZ-Bereiche als Liste (z.B. 10000-14999) — App matched automatisch.",
      "Versicherungs-PDF muss hochgeladen sein, bevor Aufträge zugewiesen werden.",
      "Service-Level (Reaktion/Garantie) erscheint im Auction-Profil.",
      "Bei Vertragsende 'inaktiv' setzen — Chronik bleibt.",
    ],
    aiSeed: "Welche Dokumente brauche ich von einem neuen Installateur?",
  },
  "admin.installer-applications": {
    title: "Installateurs-Bewerbungen prüfen",
    summary:
      "Eingegangene Bewerbungen externer Heizungsbau-Partner. Prüfe Firmendaten, " +
      "Referenzen, Versicherung, Region. Bei Annahme: Partner-Profil automatisch + " +
      "Magic-Link für ersten Login.",
    tips: [
      "Status: eingegangen / in Prüfung / angenommen / abgelehnt.",
      "Bei Ablehnung: Grund eintragen — wird als Mail gesendet.",
      "Doppelte Bewerbungen automatisch markiert (Steuer-Nr).",
      "Annahme erzeugt Partner-Profil + Onboarding-Token.",
    ],
    aiSeed: "Welche Kriterien sollte ich bei der Installateurs-Prüfung anwenden?",
  },
  "admin.routing-rules": {
    title: "Routing-Regeln – Auftragsverteilung",
    summary:
      "Regelwerk für Auftragsverteilung: nach PLZ, Heizlast, Garantie-Level, freier " +
      "Kapazität, Vertragsbindung. Bei mehreren Treffern Lead-Auction oder Direkt-Vergabe " +
      "(höchster Priority-Score).",
    tips: [
      "Reihenfolge relevant: Regel 1 zuerst, dann 2 usw.",
      "Catch-all-Regel ans Ende — sonst unzugewiesene Aufträge.",
      "Auction: alle Partner bieten — höchster Bid gewinnt.",
      "Direkt-Vergabe: erster Partner der Match erfüllt.",
      "Dry-Run vor Aktivierung: simuliere mit echten Aufträgen.",
    ],
    aiSeed: "Wann sollte ich Lead-Auction statt Direkt-Vergabe nutzen?",
  },
  "admin.pv-catalog": {
    title: "PV-Komponenten – Photovoltaik-Bausteine",
    summary:
      "Pflege der PV-Module, Wechselrichter, Speicher und Wallboxen. Wird vom PV-" +
      "Empfehlungs-Algorithmus genutzt, um zur Wärmepumpe ein passendes PV-Set " +
      "vorzuschlagen.",
    tips: [
      "Bei Preisänderungen: bestehende PV-Angebote bleiben mit altem Preis.",
      "Hersteller-Datasheet als PDF hochladen — wird im Angebot angehängt.",
      "Wirkungsgrad sauber pflegen → wirkt auf Wirtschaftlichkeit im PDF.",
      "Inaktive Komponenten verschwinden aus Editor; Altangebote bleiben.",
    ],
    aiSeed: "Welcher Wechselrichter passt zu einem 10-kWp-Set?",
  },
  "admin.pv-packages": {
    title: "PV-Pakete – Komplettsets",
    summary:
      "Vorkonfigurierte PV-Komplettpakete (Module + WR + Speicher + Montage). Werden " +
      "beim WP-Angebot als 'Add-on PV' angeboten. Größen 5/8/10/15 kWp mit je zwei " +
      "Speicher-Optionen.",
    tips: [
      "Bundle-Rabatt einkalkulieren (z.B. 8% vs. Einzelkomponenten).",
      "Große Dächer: lieber zwei Pakete kombinieren statt ein riesiges.",
      "KfW-Förderfähigkeit pflegen — wirkt auf Wirtschaftlichkeitsrechnung.",
    ],
    aiSeed: "Wann lohnt sich ein 15-kWp-Paket gegenüber zwei 8-kWp-Sets?",
  },

  // ---------------------------------------------------------------------
  // Telesales – die neue Welt
  // ---------------------------------------------------------------------
  "telesales.dashboard": {
    title: "Telesales-Dashboard – Tagesbilanz",
    summary:
      "Telesales-fokussiertes Dashboard für Director und Agents. KPIs: Anrufe heute, " +
      "kontaktiert, Wiedervorlagen offen, Conversions diese Woche. Anstehende " +
      "Wiedervorlagen und zuletzt erfasste Erfassungen im Blick. Director sieht " +
      "zusätzlich Team-Kennzahlen, Agent nur eigene. **Tab 'Orientierungsangebote'** zeigt " +
      "alle versendeten Angebote sortierbar nach Datum/Name — mit Schnell-Button 'Nachfassen', " +
      "der eine vorausgefüllte Wiedervorlage (Default +7 Tage, 10:00 Uhr) zum Anpassen anbietet. ",
    tips: [
      "Klick 'Nächster Call' startet direkt den Anruf-Modus.",
      "Wiedervorlagen 5 Min vor Fälligkeit als Push.",
      "KPI-Kacheln verlinken auf gefilterte Erfassungen.",
      "Director: zusätzliche Spalte 'Top-Agent heute' + Team-Conversion-Rate.",
      "Schulungsmodus: Dummy-Daten, keine echten Conversions.",
    ],
    aiSeed: "Wie strukturiere ich meinen Telesales-Tag optimal?",
  },
  "telesales.capture": {
    title: "Telesales-Erfassung – Lead aufnehmen",
    summary:
      "Einseitiges Formular für den Live-Anruf: Anrede, Name, Adresse, Telefon, " +
      "Heizungstyp, Verbrauch, Sanierungszustand, Wiedervorlage, Notizen. App prüft " +
      "Dubletten und vergibt Projektnummer im Format {P|W}YYMMZZ-NNNN.",
    workflow: [
      "Anrufen → Kunde antwortet → Formular von oben nach unten ausfüllen.",
      "Bei kein Interesse: Status + Notiz → Speichern → nächster Call.",
      "Bei Interesse: alle Pflichtfelder + Sanierungszustand + Heizung → Speichern.",
      "Wiedervorlage setzen → Datum/Uhrzeit → Push wird geplant.",
      "Bei Termin-Vereinbarung: Kalender-Event aus dem Formular erzeugen.",
    ],
    fields: [
      "Sanierungszustand: Unsaniert / Teilsaniert / Vollsaniert / Neubau — beeinflusst Heizlast-Schätzung.",
      "Wiedervorlage: Datum + Uhrzeit + Notiz → Web-Push 5 Min vor Termin.",
      "Heizungstyp: Öl / Gas / Pellets / Strom / Fernwärme — pflichtig bei WP-Interesse.",
      "Projektnummer: automatisch {P|W}YYMMZZ-NNNN — kein manueller Eingriff.",
    ],
    tips: [
      "Telefon im internationalen Format (+49…) für Click-to-Call.",
      "Dubletten zeigt App als gelbe Warnung.",
      "Notizfeld ist Markdown-fähig.",
      "Speichern als Lead → inbound_leads (NICHT Projekt).",
      "Push kommt nur, wenn im Profil Web-Push erlaubt.",
    ],
    aiSeed: "Welche Fragen muss ich bei einem Erstgespräch unbedingt klären?",
  },
  "telesales.director": {
    title: "Telesales-Director-Cockpit",
    summary:
      "Director-Übersicht über alle Agents im eigenen Telesales-Team: Anrufe, " +
      "Conversions, Wiedervorlagen. Coachingrelevante KPIs: ø Calls/Tag, Conversion-" +
      "Rate, Gesprächsdauer, Wiedervorlagen-Quote, No-Show-Rate.",
    tips: [
      "Klick auf Agent öffnet Detail-Analytik mit Trend-Linien (4 Wochen).",
      "Niedrige Conversion < 10%: Coaching-Push direkt erzeugen.",
      "Listen-Zuweisung über Admin → Call-Listen.",
      "Director hat KEINE Sales-Rep-Kennzahlen — saubere Fokussierung.",
      "Bei Agent-Urlaub: Listen temporär anderem Agent zuweisen.",
    ],
    aiSeed: "Welche KPI ist für Telesales-Coaching am wichtigsten?",
  },
  "telesales.next-call": {
    title: "Nächster Anruf – Telesales-Flow-Modus",
    summary:
      "Konzentrierter Anruf-Modus: eine Karte mit Vorname, Nachname, PLZ, Telefon, " +
      "Vorgeschichte. Status-Buttons + Notizfeld + 'Nächster' führen dich durch deine " +
      "Liste.",
    workflow: [
      "Anrufen (Click-to-Dial sobald Twilio Voice aktiv).",
      "Während Gespräch: Status (erreicht/nicht erreicht/kein Interesse/Wiedervorlage/interessiert).",
      "'Interessiert' → automatisch in Telesales-Capture.",
      "'Wiedervorlage' → Datum + Notiz → Push geplant.",
      "Nach Speichern: 'Nächster' → nächste Nummer.",
    ],
    tips: [
      "Tastatur-Shortcuts (Desktop): N=Nächster, R=Wiedervorlage, I=Interessiert, X=Kein Interesse.",
      "Bei Anrufpausen App offen lassen — sie kennt deinen Listen-Cursor.",
      "Wiedervorlagen 5 Min vorher als Pop-up + Push.",
      "Sprachnotiz aufnehmen → Whisper transkribiert automatisch.",
      "Pro Anruf ein Submission-Audit-Eintrag — keine verlorenen Daten.",
    ],
    aiSeed: "Wie reagiere ich auf den Einwand 'Ich habe kein Interesse'?",
  },

  // ---------------------------------------------------------------------
  // Manager-Cockpits
  // ---------------------------------------------------------------------
  "manager.pipeline-health": {
    title: "Pipeline-Health – Frühwarn-System",
    summary:
      "Direktor-/Manager-Sicht auf alle Risiko-Indikatoren: stille Projekte (>14d ohne " +
      "Aktivität), überfällige Wiedervorlagen, blockierte Back-Office-Steps, AZ1/AZ2-" +
      "Mahnstufen, Win-Rate-Einbrüche. Wöchentlicher Mail-Digest jeden Montag 07:00.",
    tips: [
      "Rote KPIs verlinken direkt auf betroffene Projekte.",
      "Schwellenwerte unter Sales-Settings anpassbar (Default 7d/14d).",
      "Button 'Digest jetzt senden' triggert sofortigen Test-Mail-Versand.",
      "Bei großen Teams: Filter nach Region/Vertriebsleiter.",
      "Stille Projekte aufklappen → Top-Aktion vorschlagen.",
    ],
    aiSeed: "Wie senke ich die Anzahl stiller Projekte in meinem Team?",
  },
  "manager.sales-settings": {
    title: "Sales-Einstellungen",
    summary:
      "Konfiguration vertriebsspezifischer Werte: Heizlast-Schwellen für Auto-Paket-" +
      "Vorschläge, Default-Provisionen, Pipeline-Health-Schwellen, Lead-Routing-Default, " +
      "Mail-Templates für Angebot/Nachfass/Status.",
    tips: [
      "Provisionssatz pro Rolle (Vertrieb/Leiter/Direktor) separat einstellbar.",
      "Mail-Templates akzeptieren Variablen wie {{customer.first_name}} und {{project.number}}.",
      "Pipeline-Health-Schwellen: gelb 7d / orange 14d / rot 21d (Default).",
      "Default-Lead-Routing: per PLZ → Vertriebler X / Region Y.",
    ],
    aiSeed: "Welche Provisionsstaffelung ist branchenüblich?",
  },

  // ---------------------------------------------------------------------
  // Lead-Auctions & Installateur-Welt
  // ---------------------------------------------------------------------
  "lead-auctions": {
    title: "Lead-Auctions – Auftragsbieten",
    summary:
      "Marktplatz, auf dem qualifizierte Wärmepumpen-Leads an meistbietende Installateurs-" +
      "Partner versteigert werden. Pro Lead: Bid-Frist, Mindestgebot, Match-Kriterien. " +
      "Nach Frist gewinnt das höchste gültige Gebot.",
    tips: [
      "Mindestgebot deckt mindestens deine Lead-Akquisekosten — Default 80-150 EUR.",
      "Bid-Frist: 48h Standard, bei dringenden Leads 12h.",
      "Gewinner sieht die vollen Lead-Daten erst nach Bezahlung.",
      "Anti-Bot-Schutz: jede Bid-IP geprüft.",
      "Auktion abgesagt? Bieter bekommen volle Erstattung.",
    ],
    aiSeed: "Wie lege ich ein realistisches Mindestgebot für einen Lead fest?",
  },
  "installer.jobs": {
    title: "Meine Aufträge (Installateur-Queue)",
    summary:
      "Persönliche Auftragsliste für Installateure. Alle dir zugewiesenen WP-Aufträge " +
      "mit Adresse, Heizlast, Komponentenliste und Foto-Upload für die Inbetriebnahme. " +
      "Preise und interne Notizen ausgeblendet.",
    workflow: [
      "Auftrag anklicken → Detailansicht mit Adresse, Kontakt, Tech-Check-Daten.",
      "'Installation gestartet' → Status 'in Arbeit', Back-Office bekommt Push.",
      "Fotos hochladen (Vorzustand, Installation, Außeneinheit, Schaltschrank, Typenschilder).",
      "Inbetriebnahme-Protokoll als PDF → Status 'Inbetriebnahme erledigt'.",
      "'Auftrag abschließen' → Übergabe-Protokoll mit Kunden-Unterschrift.",
    ],
    tips: [
      "Du siehst NUR dir zugewiesene Aufträge — keine Preise.",
      "Fotos werden direkt ans Projekt angehängt.",
      "Notiz im Auftragschat: Vertrieb + Back-Office bekommen Push.",
      "Inbetriebnahme-PDF idealerweise gescanntes Original (DIN A4).",
      "Status 'blockiert' bei Materialfehler/Kunden-Verschiebung.",
      "Push aktivieren — neue Aufträge direkt aufs Handy.",
      "Offlinefähig: Fotos werden lokal zwischengespeichert.",
    ],
    aiSeed: "Welche Pflicht-Fotos muss ich bei der Inbetriebnahme hochladen?",
  },
  "installer.auctions": {
    title: "Lead-Auctions (Bieter-View)",
    summary:
      "Als Installateurs-Partner siehst du offene Lead-Auctions: Region, Heizlast-" +
      "Schätzung, Mindestgebot, Auktion-Ende. Bieten + Tracking aller eigenen Bids.",
    tips: [
      "Bid-Höhe: Marge minus Akquise-Kosten.",
      "Auto-Bid: max. Gebot definieren — System bietet inkrementell mit.",
      "Nach Gewinn: 24h Reaktionszeit sonst Pönale.",
      "Bid-Historie zeigt deine Win-Rate.",
      "Push bei neuem Lead in deiner Region aktivieren.",
    ],
    aiSeed: "Welche Strategie funktioniert bei kompetitiven Bietern am besten?",
  },
  "installer.onboarding": {
    title: "Onboarding für neue Installateurs-Partner",
    summary:
      "Geführter Einrichtungs-Flow für frisch angenommene Heizungsbau-Partner: " +
      "Firmen-Daten, Versicherungs-PDF, Service-Region, Auftragsverarbeitungsvertrag " +
      "digital unterschreiben.",
    tips: [
      "Nach Abschluss: Zugang zu Job-Queue + Lead-Auctions.",
      "Versicherungs-PDF wird vom Admin freigeschaltet — bis dahin Bids gesperrt.",
      "AVV ist DSGVO-Pflicht — kein Auftrag ohne Unterschrift.",
      "Bei Fragen support@novatussales.pro kontaktieren.",
    ],
    aiSeed: "Was muss ich beim ersten Login als neuer Installateur tun?",
  },

  // ---------------------------------------------------------------------
  // Projekt-spezifisch
  // ---------------------------------------------------------------------
  "project.workflow": {
    title: "Auftrags-Workflow (read-only)",
    summary:
      "Nach Übergabe ans Back-Office siehst du als Vertrieb hier den aktuellen Stand " +
      "deines Auftrags entlang der 17 Schritte. Read-only — Bearbeitung übernimmt Back-" +
      "Office. Bei Statuswechsel bekommst du Push.",
    tips: [
      "Sichtbar erst NACH 'An Back-Office übergeben'.",
      "Farbcode: grün=erledigt, blau=in Arbeit, grau=ausstehend, rot=blockiert.",
      "Klick auf Schritt zeigt Datum, Verantwortlichen und Notiz.",
      "Erinnerungen für Kunden (z.B. 'AZ1 fällig') erzeugt Back-Office automatisch.",
      "Bei Eskalation (AZ1 > 14d offen): Vertriebsleiter bekommt zusätzlich Mail.",
    ],
    aiSeed: "Was passiert, wenn der Kunde die AZ1-Rechnung nicht zahlt?",
  },
  "projects.new": {
    title: "Neues Projekt anlegen",
    summary:
      "Anlage eines neuen Wärmepumpen-Projekts: Pflichtfelder im ersten Schritt (Anrede, " +
      "Name, Adresse, Telefon, Eigentümer-Status, Gebäudetyp). App vergibt automatisch " +
      "Kundennummer + Projektnummer im Format {P|W}YYMMZZ-NNNN.",
    workflow: [
      "Anrede + Name + Adresse + Telefon erfassen.",
      "Eigentümer-Status wählen — für KfW-Förderung relevant.",
      "Gebäudetyp (EFH/DHH/RH/MFH) → steuert WP-Paket-Default.",
      "'Anlegen' → Projekt in Liste, direkt in Tab 'Kunde'.",
      "Nächster Schritt: Tech-Check vor Ort → Verbrauch → Angebot.",
    ],
    tips: [
      "Telefon im internationalen Format (+49…) für SMS + Click-to-Call.",
      "Projektnummer automatisch vergeben.",
      "Bei MFH: später im Tech-Check 'Heizkreise' korrekt erfassen.",
      "Land per Default Deutschland — AT/CH manuell anpassen.",
      "Pflichtfelder rot umrandet — Speichern blockiert bis ausgefüllt.",
    ],
    aiSeed: "Welche Daten brauche ich vom Kunden für ein erfolgreiches Erstgespräch?",
  },

  // ---------------------------------------------------------------------
  // User-zentrische Bereiche
  // ---------------------------------------------------------------------
  "profile": {
    title: "Mein Profil",
    summary:
      "Deine persönlichen Daten, Push-Benachrichtigungen, Sicherheits-Einstellungen " +
      "(WebAuthn/2FA/Magic-Link), Kalender-Abo-URL und Vertretungs-Regelung. Änderungen " +
      "sind sofort wirksam.",
    tips: [
      "Push aktivieren: ohne verpasst du Statuswechsel und Wiedervorlagen.",
      "WebAuthn (Touch-ID/Fingerprint) ersetzt OTP — schneller + sicherer.",
      "Bei Urlaub: Vertretung eintragen → Wiedervorlagen gehen temporär an Kollegen.",
      "Kalender-Abo-URL: kopieren + in Outlook/Google/Apple einfügen.",
      "Token-Reset: setzt Abo-URL zurück (falls geleakt).",
      "Avatar-Bild erscheint in Nachrichten + Kanban-Karten.",
    ],
    aiSeed: "Wie aktiviere ich Touch-ID statt OTP für den Login?",
  },
  "abo": {
    title: "Abo & Rechnungen",
    summary:
      "Übersicht deiner Plattform-Abrechnung: aktiver Plan, nächste Rechnung, " +
      "Zahlungsmittel, letzte Rechnungen als PDF zum Download.",
    tips: [
      "Plan-Wechsel sofort wirksam — Pro-Rata-Abrechnung auf nächster Rechnung.",
      "Bei Zahlungsausfall: 3 Erinnerungen, dann Pause (kein Datenverlust).",
      "Rechnungen GoBD-konform als PDF gespeichert.",
      "Karten-Wechsel: alte ersetzt, keine Doppel-Buchungen.",
      "Bei Fragen support@novatussales.pro kontaktieren.",
    ],
    aiSeed: "Wie kann ich auf den Professional-Plan upgraden?",
  },
  "select-plan": {
    title: "Plan wählen",
    summary:
      "Vergleich der Plattform-Pläne: Basic (bis 5 User, Kern-Funktionen), Professional " +
      "(bis 25 User, Telesales + Back-Office), Enterprise (unbegrenzt + Custom-Branding). " +
      "Wechsel jederzeit möglich, Pro-Rata-Abrechnung.",
    tips: [
      "Basic ist 14 Tage kostenlos zur Erprobung.",
      "Enterprise: Custom-Branding, eigener SMTP, Premium-Support.",
      "Anzahl User = aktive Konten (inaktive zählen nicht).",
      "Bei Wechsel: Daten 1:1 übernommen.",
    ],
    aiSeed: "Welcher Plan passt zu meiner Vertriebsorganisation?",
  },
  "quiz": {
    title: "Online-Quiz & Zertifikat",
    summary:
      "Multiple-Choice-Quiz zur Wärmepumpen-Fachkenntnis. Nach bestandenem Quiz (>=80%) " +
      "erhältst du ein PDF-Zertifikat. Beliebig wiederholbar — bester Score zählt.",
    tips: [
      "15 zufällige Fragen aus Pool von ~80.",
      "Pro Frage 60 Sekunden.",
      "Bei Falsch-Antworten: Lösung + Erklärung (Lernmodus).",
      "Zertifikat-PDF wird per Mail gesendet.",
      "Manager sehen Quiz-Ergebnisse des Teams unter Admin → Quiz-Ergebnisse.",
    ],
    aiSeed: "Welche Themen werden im Quiz abgefragt?",
  },
  "feedback": {
    title: "Feedback geben",
    summary:
      "Feedback an Novatus-Team senden: Bug, Feature-Wunsch, Anmerkung. Screenshot " +
      "automatisch beigefügt. Antwort-Zeit ø 24h.",
    tips: [
      "Bei Bugs: Schritte zur Reproduktion mitgeben — beschleunigt Fix.",
      "Feature-Wünsche werden im Backlog priorisiert (P0/P1/P2).",
      "Feedback ist NICHT anonym — Antwort per E-Mail.",
      "Bei sensiblen Themen direkt an support@novatussales.pro.",
    ],
    aiSeed: "Was sollte ich in einen guten Bug-Report schreiben?",
  },
  "register": {
    title: "Registrierung – Konto erstellen",
    summary:
      "Self-Service-Registrierung für neue Vertriebler: Mobilnummer + OTP → Profil " +
      "(Name, E-Mail, Adresse, Bank für Provisionen) → Admin-Freigabe → Login. " +
      "Bestätigungs-Mail mit Welcome-Pack folgt.",
    tips: [
      "Mobil im internationalen Format (+49…).",
      "OTP gilt 10 Minuten.",
      "Bei Wartezeit auf Admin-Freigabe: Push sobald frei.",
      "E-Mail optional aber empfohlen für Angebots-Versand.",
      "Bankdaten verschlüsselt — nur Admin sieht sie zur Provisionsabrechnung.",
    ],
    aiSeed: "Wie lange dauert die Admin-Freigabe nach der Registrierung?",
  },
};

Object.entries(AUDIENCE_PATCH).forEach(([k, a]) => { if (BASE_HELP[k]) BASE_HELP[k] = { ...BASE_HELP[k], audience: a }; });

export const HELP_CONTENT = { ...BASE_HELP, ...SALES_HELP };

/**
 * Pfad-basierte Default-Auflösung – wird verwendet, wenn KEINE feinere
 * Kontextquelle (z. B. Project-Tab) verfügbar ist.
 */
export function helpContextFromPath(pathname = "") {
  // -------- Admin --------
  if (pathname.startsWith("/admin/test-data")) return "admin.test-data";
  if (pathname.startsWith("/admin/presence")) return "admin.presence";
  if (pathname.startsWith("/admin/activity")) return "admin.activity";
  if (pathname.startsWith("/admin/audit-log") || pathname.startsWith("/admin/security-audit") || pathname.startsWith("/admin/audit-scores")) return "admin.audit-log";
  if (pathname.startsWith("/admin/call-lists")) return "admin.call-lists";
  if (pathname.startsWith("/admin/back-office")) return "admin.back-office";
  if (pathname.startsWith("/admin/wp-catalog")) return "admin.wp-catalog";
  if (pathname.startsWith("/admin/pv-catalog")) return "admin.pv-catalog";
  if (pathname.startsWith("/admin/pv-packages")) return "admin.pv-packages";
  if (pathname.startsWith("/admin/promo-codes")) return "admin.promo-codes";
  if (pathname.startsWith("/admin/orientierungsangebote")) return "admin.inbound-leads";
  if (pathname.startsWith("/admin/users-table")) return "admin.users-table";
  if (pathname.startsWith("/admin/users")) return "admin.users";
  if (pathname.startsWith("/admin/branches")) return "admin.branches";
  if (pathname.startsWith("/admin/invoice-tools")) return "admin.invoice-tools";
  if (pathname.startsWith("/admin/email-logs")) return "admin.email-logs";
  if (pathname.startsWith("/admin/document-templates")) return "admin.document-templates";
  if (pathname.startsWith("/admin/text-blocks")) return "admin.text-blocks";
  if (pathname.startsWith("/admin/vcheck-pflichtfelder")) return "admin.vcheck-rules";
  if (pathname.startsWith("/admin/sachbearbeiter-stats")) return "admin.sachbearbeiter-stats";
  if (pathname.startsWith("/admin/signature-audit")) return "admin.signature-audit";
  if (pathname.startsWith("/admin/project-stages")) return "admin.project-stages";
  if (pathname.startsWith("/admin/manual-signatures")) return "admin.manual-signatures";
  if (pathname.startsWith("/admin/quiz-results")) return "admin.quiz-results";
  if (pathname.startsWith("/admin/produkte")) return "admin.produkte";
  if (pathname.startsWith("/admin/offers")) return "admin.offers";
  if (pathname.startsWith("/admin/hierarchy")) return "admin.hierarchy";
  if (pathname.startsWith("/admin/empfehlungsbonus")) return "admin.empfehlungsbonus";
  if (pathname.startsWith("/admin/auction-stats")) return "admin.auction-stats";
  if (pathname.startsWith("/admin/sms-stats")) return "admin.sms-stats";
  if (pathname.startsWith("/admin/briefing-ab-stats")) return "admin.briefing-ab";
  if (pathname.startsWith("/admin/packages")) return "admin.packages";
  if (pathname.startsWith("/admin/system-health")) return "admin.system_health";
  if (pathname.startsWith("/admin/health")) return "admin.health";
  if (pathname.startsWith("/admin/installer-applications")) return "admin.installer-applications";
  if (pathname.startsWith("/admin/installers")) return "admin.installers";
  if (pathname.startsWith("/admin/partner-profiles")) return "admin.partner-profiles";
  if (pathname.startsWith("/admin/routing-rules")) return "admin.routing-rules";
  if (pathname.startsWith("/admin/handbook")) return "admin.handbook";
  if (pathname.startsWith("/admin/tenants")) return "admin.tenants";
  if (pathname.startsWith("/admin/api-keys")) return "admin.api-keys";
  if (pathname.startsWith("/admin/compliance")) return "admin.compliance";
  if (pathname.startsWith("/admin/penalties")) return "admin.penalties";
  if (pathname.startsWith("/admin/telesales-settings")) return "telesales.director";
  if (pathname.startsWith("/admin/feedback")) return "admin.feedback";

  // -------- Super-Admin --------
  if (pathname.startsWith("/superadmin/tenants")) return "superadmin.tenants";

  // -------- Manager / Direktor --------
  if (pathname.startsWith("/manager/kanban")) return "manager.kanban";
  if (pathname.startsWith("/kanban-bza")) return "kanban.bza";
  if (pathname.startsWith("/pipeline/intake")) return "pipeline.intake";
  if (pathname.startsWith("/katalog")) return "katalog";
  if (pathname.startsWith("/manager/team")) return "manager.team";
  if (pathname.startsWith("/manager/sales-settings")) return "manager.sales-settings";

  // -------- Telesales --------
  if (pathname.startsWith("/telesales/capture")) return "telesales.capture";
  if (pathname.startsWith("/telesales/director")) return "telesales.director";
  if (pathname.startsWith("/telesales/next-call")) return "telesales.next-call";
  if (pathname.startsWith("/telesales")) return "telesales.dashboard";

  // -------- Installateur --------
  if (pathname.startsWith("/installer/jobs")) return "installer.jobs";
  if (pathname.startsWith("/installer/auctions")) return "installer.auctions";
  if (pathname.startsWith("/installer/onboarding")) return "installer.onboarding";

  // -------- Lead-Auctions Marketplace --------
  if (pathname.startsWith("/lead-auctions")) return "lead-auctions";

  // -------- User-zentrische Bereiche --------
  if (pathname.startsWith("/profile")) return "profile";
  if (pathname.startsWith("/abo")) return "abo";
  if (pathname.startsWith("/select-plan") || pathname.startsWith("/checkout")) return "select-plan";
  if (pathname.startsWith("/quiz")) return "quiz";
  if (pathname.startsWith("/feedback")) return "feedback";
  if (pathname.startsWith("/register/pending")) return "register.pending";
  if (pathname.startsWith("/register")) return "register";

  // -------- Tagesübersicht --------
  if (pathname.startsWith("/calendar")) return "calendar";
  if (pathname.startsWith("/tasks")) return "tasks";

  // -------- Projekte --------
  if (pathname.startsWith("/projects/new")) return "projects.new";
  if (/^\/projects\/[^/]+\/pv/.test(pathname)) return "project.pv";
  if (pathname.startsWith("/projects/") && pathname.split("/").length >= 3) {
    return "project.customer";
  }
  if (pathname.startsWith("/projects")) return "projects.list";

  // -------- Direktnachrichten --------
  if (pathname.startsWith("/messages/")) return "messages.direct";

  if (pathname.startsWith("/dashboard")) return "dashboard";
  return "app.global";
}
